/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useState, useEffect, Suspense } from 'react';

// Mantine
import { Box } from '@mantine/core';

// Routing
import { Outlet, useNavigate } from 'react-router';

// Layouts
import { Header } from '@layouts/Header';
import { Footer } from '@layouts/Footer';
import { Sidebar } from '@layouts/Sidebar';

// Hooks
import { useLocation } from 'react-router';
import { useLogout } from '@hooks/useLogout';

// Config
import packageJson from '../../../../../package.json';
import { IS_STAGING } from '@config/constants/env';

// Styles
import classes from './main.module.css';

// Components
import { DefaultModal } from '@components/molecules/DefaultModal';
import { Loading } from '@components/atoms/Loading';
import { BUILD_SHA } from '@config/constants/env';
import { StartMint } from '@features/sourcing/productions/components/StartMint';

// Store
import { useTranslation } from 'react-i18next';

// Icons
import {
	IconOverviewActive,
	IconOverviewInActive,
	IconContributionsActive,
	IconContributionsInActive,
	IconUsersActive,
	IconUsersInActive,
	IconMintActive,
	IconMintInActive,
	IconSupportActive,
	IconSupportInActive,
	IconLogoutActive,
	IconLogoutInActive,
	IconCalculatorActive,
	IconCalculatorInActive,
	IconMeasurabilityInActive,
	IconMeasurabilityActive,
	IconAdditionalityInActive,
	IconAdditionalityActive,
	IconDatabaseActive,
	IconDatabaseInActive,
	IconPressureLightActive,
	IconPressureLightInActive,
	IconSourcingActive,
	IconSourcingInActive,
	IconHexagonActive,
	IconHexagonInActive,
	IconSiteActive,
	IconSiteInActive,
	IconImportHistoryActive,
	IconImportHistoryInActive,
	IconBrandInActive,
	IconBrandActive,
	IconProducerInActive,
	IconProducerActive,
	IconOrganizationActive,
	IconOrganizationInActive,
} from '@layouts/Sidebar/Icons';

import { useAuthorization } from '@lib/authorization';
import { ROLES } from '@config/constants/roles';
import { AddMeterPoints } from '@features/resources/meter-points/components/AddMeterPoints';
import { AddSite } from '@features/resources/sites/components/AddSite/AddSite';

import { useMeterPoint } from '@features/resources/meter-points/hooks/useMeterPoint';
import { useSites } from '@features/resources/sites/hooks/useSites';
import Overview from '@app/routes/app/overview/overview';
import useStore from '@store/index';
import { useStatus } from '@api/get-version';
import { DrawerStepperOffset } from '@features/offsets/components/DrawerStepperOffset';
import { TSidebarItem } from '@features/main/types';
import { useWhoAmI } from '@api/get-whoami';
import { AREC_ORGANIZATION_IDS } from '@config/constants/organizations';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const Main = () => {
	/* ********************************** Hooks ********************************* */
	const {
		setIsOpenStartMint,
		isOpenStartMint,
		setMintHistorySelected,
		getUser,
		openDrawerMeterPointForm,
		openDrawerSiteForm,
		offsetOpenDrawer,
		setOffsetCreationActiveStep,
		setOffsetOpenDrawer,
		setOffsetAdditionalProjects,
		setOffsetAdditionalProjectsSelected,
		setOffsetMesurabilityProjects,
		setOffsetMesurabilityProjectsSelected,
		setOffsetProjectMixinAco2,
		setOffsetProjectMixinAddi,
		setOffsetStep2Error,
		setOffsetStep3Error,
		setOffsetCreationDatas,
		setOffsetMesurabilityProjectsPage,
		setOffsetAdditionalProjectsPage,
	} = useStore();

	const { data: cachedUser } = useWhoAmI();

	const user = getUser();

	const { checkRole, checkAccess } = useAuthorization();

	const mainLinksData: TSidebarItem[] = [
		{
			id: 'overview',
			icon: IconOverviewActive,
			iconInactive: IconOverviewInActive,
			text: 'sidebar.overview',
			path: '/',
			group: 'sidebar.main',
			visible: true,
		},
		{
			id: 'account-settings',
			icon: IconOverviewActive,
			iconInactive: IconOverviewInActive,
			text: 'sidebar.accountSettings',
			path: '/account-settings',
			group: 'sidebar.main',
			visible: false,
		},
		checkAccess({
			allowedRoles: [
				ROLES.ADMIN,
				ROLES.SUPERADMIN,
				ROLES.STAFF,
				ROLES.USER,
				ROLES.ORGANIZATION_ADMIN,
				ROLES.ORGANIZATION_MEMBER,
				ROLES.ORGANIZATION_STAFF,
				ROLES.BRAND_ADMIN,
				ROLES.BRAND_STAFF,
			],
		}) &&
			typeof user !== 'boolean' && {
				id: 'offsets',
				icon: IconContributionsActive,
				iconInactive: IconContributionsInActive,
				text: 'sidebar.offsets',
				path: '/offsets',
				group: 'sidebar.main',
				visible: true,
			},

		typeof user !== 'boolean' &&
			checkAccess({
				allowedRoles: [
					ROLES.ADMIN,
					ROLES.SUPERADMIN,
					ROLES.STAFF,
					ROLES.ORGANIZATION_ADMIN,
					ROLES.ORGANIZATION_MEMBER,
					ROLES.ORGANIZATION_STAFF,
					ROLES.PRODUCER_ADMIN,
					ROLES.PRODUCER_STAFF,
					ROLES.BRAND_ADMIN,
					ROLES.BRAND_STAFF,
				],
			}) && {
				id: 'projects-measurability',
				text: 'global.measurabilities',
				path: '/measurability-projects',
				group: 'sidebar.main',
				icon: IconMeasurabilityActive,
				iconInactive: IconMeasurabilityInActive,
				visible: true,
			},
		typeof user !== 'boolean' &&
			checkAccess({
				allowedRoles: [
					ROLES.ADMIN,
					ROLES.SUPERADMIN,
					ROLES.STAFF,
					ROLES.ORGANIZATION_ADMIN,
					ROLES.ORGANIZATION_MEMBER,
					ROLES.ORGANIZATION_STAFF,
					ROLES.BRAND_ADMIN,
					ROLES.BRAND_STAFF,
				],
			}) && {
				id: 'projects-additionality',
				text: 'global.additionalitiesProjects',
				path: '/additionality-projects',
				group: 'sidebar.main',
				icon: IconAdditionalityActive,
				iconInactive: IconAdditionalityInActive,
				visible: true,
			},

		checkRole([
			ROLES.ORGANIZATION_ADMIN,
			ROLES.ORGANIZATION_STAFF,
		]) && {
			id: 'databases-producers',
			text: 'global.producers',
			path: '/producers',
			group: 'sidebar.main',
			icon: IconProducerActive,
			iconInactive: IconProducerInActive,
			visible: true,
		},
		checkRole([
			ROLES.ORGANIZATION_ADMIN,
			ROLES.ORGANIZATION_STAFF,
		]) && {
			id: 'databases-brands',
			text: 'sidebar.brands',
			path: '/brands',
			group: 'sidebar.main',
			icon: IconBrandActive,
			iconInactive: IconBrandInActive,
			visible: true,
		},
		checkRole([
			ROLES.PRODUCER_ADMIN,
			ROLES.PRODUCER_STAFF,
			ROLES.ORGANIZATION_ADMIN,
			ROLES.ORGANIZATION_STAFF,
		]) && {
			id: 'sourcing-productions',
			text: 'sidebar.productions',
			path: '/productions',
			group: 'sidebar.main',
			icon: IconHexagonActive,
			iconInactive: IconHexagonInActive,
			visible: true,
		},

		checkAccess({
			allowedRoles: [
				ROLES.ADMIN,
				ROLES.SUPERADMIN,
				ROLES.STAFF,
			],
		}) && {
			id: 'databases',
			visible: true,
			icon: IconDatabaseActive,
			iconInactive: IconDatabaseInActive,
			text: 'sidebar.databases',
			group: 'Admin',
			subMenu: [
				...(checkAccess({
					allowedRoles: [
						ROLES.ADMIN,
						ROLES.SUPERADMIN,
						ROLES.STAFF,
					],
				})
					? [
							{
								id: 'databases-api-tokens',
								text: 'sidebar.apiTokens',
								path: '/api-tokens',
								icon: IconUsersInActive,
								iconInactive: IconUsersActive,
							},
						]
					: []),
				...(checkAccess({
					allowedRoles: [
						ROLES.ADMIN,
						ROLES.SUPERADMIN,
						ROLES.STAFF,
					],
				})
					? [
							{
								id: 'databases-organizations',
								text: 'sidebar.organizations',
								path: '/organizations',
								icon: IconOrganizationInActive,
								iconInactive: IconOrganizationActive,
							},
						]
					: []),
				...(checkAccess({
					allowedRoles: [
						ROLES.ADMIN,
						ROLES.SUPERADMIN,
						ROLES.STAFF,
						ROLES.ORGANIZATION_ADMIN,
						ROLES.ORGANIZATION_STAFF,
					],
				})
					? [
							{
								id: 'databases-brands',
								text: 'sidebar.brands',
								path: '/brands',
								icon: IconBrandInActive,
								iconInactive: IconBrandActive,
							},
						]
					: []),
				...(checkAccess({
					allowedRoles: [
						ROLES.ADMIN,
						ROLES.SUPERADMIN,
						ROLES.STAFF,
						ROLES.ORGANIZATION_ADMIN,
						ROLES.ORGANIZATION_STAFF,
					],
				})
					? [
							{
								id: 'databases-producers',
								text: 'global.producers',
								path: '/producers',
								icon: IconProducerInActive,
								iconInactive: IconProducerActive,
							},
						]
					: []),
				...(checkAccess({
					allowedRoles: [
						ROLES.ADMIN,
						ROLES.SUPERADMIN,
						ROLES.STAFF,
					],
				})
					? [
							{
								id: 'databases-measurabilities',
								text: 'global.measurabilitiesProjects',
								path: '/measurabilities',
								icon: IconMeasurabilityInActive,
								iconInactive: IconMeasurabilityActive,
							},
						]
					: []),
				...(checkAccess({
					allowedRoles: [
						ROLES.ADMIN,
						ROLES.SUPERADMIN,
						ROLES.STAFF,
					],
				})
					? [
							{
								id: 'databases-additionalities',
								text: 'global.additionalitiesProjects',
								path: '/additionalities',
								icon: IconAdditionalityInActive,
								iconInactive: IconAdditionalityActive,
							},
						]
					: []),
				...(checkAccess({
					allowedRoles: [
						ROLES.ADMIN,
						ROLES.SUPERADMIN,
						ROLES.STAFF,
					],
				})
					? [
							{
								id: 'databases-sites',
								text: 'sidebar.sites',
								path: '/sites',
								icon: IconSiteInActive,
								iconInactive: IconSiteActive,
							},
						]
					: []),
				...(checkAccess({
					allowedRoles: [
						ROLES.ADMIN,
						ROLES.SUPERADMIN,
						ROLES.STAFF,
					],
				})
					? [
							{
								id: 'databases-conversion-factors',
								text: 'sidebar.conversionFactors',
								path: '/conversion-factors',
								icon: IconCalculatorInActive,
								iconInactive: IconCalculatorActive,
							},
						]
					: []),
				...(checkAccess({
					allowedRoles: [
						ROLES.ADMIN,
						ROLES.SUPERADMIN,
						ROLES.STAFF,
					],
				})
					? [
							{
								id: 'databases-meter-points',
								text: 'global.meterPoints',
								path: '/meter-points',
								icon: IconPressureLightInActive,
								iconInactive: IconPressureLightActive,
							},
						]
					: []),
				...(checkAccess({
					allowedRoles: [
						ROLES.ADMIN,
						ROLES.SUPERADMIN,
						ROLES.STAFF,
					],
				})
					? [
							{
								id: 'databases-manage-users',
								text: 'global.users',
								path: '/users',
								icon: IconUsersInActive,
								iconInactive: IconUsersActive,
							},
						]
					: []),
			],
		},
		checkAccess({
			allowedRoles: [
				ROLES.ADMIN,
				ROLES.SUPERADMIN,
				ROLES.STAFF,
			],
		}) &&
			!Object.values(AREC_ORGANIZATION_IDS).includes(
				user?.organization?.id
			) && {
				id: 'sourcing',
				visible: true,
				icon: IconSourcingActive,
				iconInactive: IconSourcingInActive,
				text: 'sidebar.sourcing',
				group: 'Admin',
				subMenu: [
					...(checkAccess({
						allowedRoles: [
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
							ROLES.ORGANIZATION_ADMIN,
							ROLES.ORGANIZATION_STAFF,
							ROLES.PRODUCER_ADMIN,
							ROLES.PRODUCER_STAFF,
						],
					})
						? [
								{
									id: 'sourcing-productions',
									text: 'sidebar.productions',
									path: '/productions',
									icon: IconHexagonInActive,
									iconInactive: IconHexagonActive,
								},
							]
						: []),
					...(checkAccess({
						allowedRoles: [
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
						],
					})
						? [
								{
									id: 'sourcing-mint-history',
									text: 'sidebar.mintHistory',
									path: '/productions/mint-history',
									icon: IconMintInActive,
									iconInactive: IconMintActive,
								},
							]
						: []),
					...(checkAccess({
						allowedRoles: [
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
						],
					})
						? [
								{
									id: 'sourcing-import-history',
									text: 'sidebar.importHistory',
									path: '/productions/import-history',
									icon: IconImportHistoryInActive,
									iconInactive: IconImportHistoryActive,
								},
							]
						: []),
				],
			},
		!Object.values(AREC_ORGANIZATION_IDS).includes(
			user?.organization?.id
		) && {
			id: 'support',
			visible: true,
			icon: IconSupportActive,
			iconInactive: IconSupportInActive,
			text: 'sidebar.support',
			path: '/support',
			group: 'sidebar.others',
			disabled: true,
		},
		{
			id: 'logout',
			visible: true,
			icon: IconLogoutActive,
			iconInactive: IconLogoutInActive,
			text: 'sidebar.logout',
			path: '/',
			group: 'sidebar.others',
		},
	];

	const { handleCloseDrawerAddMeterPoint } =
		useMeterPoint();

	const { handleCloseDrawerAddSite } = useSites();
	const [isOpenModalLogout, setIsOpenModalLogout] =
		useState(false);
	const navigate = useNavigate();
	const location = useLocation();
	const logout = useLogout();
	const { version, build } = useStatus();
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);

	const flattenedSidebarItems: any =
		mainLinksData.flatMap((item: any) => {
			if (item.subMenu) {
				return [
					{
						id: item.id,
						path: item.path,
						label: item.text,
						icon: item.icon,
						iconActive: item.iconActive,
						group: item.group,
					},
					...(item.subMenu &&
						item.subMenu.map((link: any) => ({
							id: link.id,
							path: link.path,
							label: link.text,
							icon1: link.icon1,
							iconActive1: link.iconActive1,
						}))),
				];
			}
			return {
				id: item.id,
				path: item.path,
				label: item.text,
				icon: item.icon,
				iconActive: item.iconActive,
				group: item.group,
			};
		});

	const getCurrentPathWithoutLastPart = () => {
		if (
			location.pathname.startsWith(
				'/measurability-projects'
			)
		) {
			return '/measurability-projects';
		}

		if (
			location.pathname.startsWith(
				'/additionality-projects'
			)
		) {
			return '/additionality-projects';
		}

		const path = /\/([a-fA-F0-9]{24})$/.test(
			location.pathname
		)
			? location.pathname.slice(
					0,
					location.pathname.lastIndexOf('/')
				)
			: location.pathname;

		return path;
	};

	const getModule = () => {
		const module = flattenedSidebarItems.find(
			(item: any) =>
				item.path == getCurrentPathWithoutLastPart()
		);

		// Update label of module
		if (module) {
			switch (module.id) {
				case 'projects-measurability':
					return {
						...module,
						label: 'global.measurabilitiesProjects',
					};
				case 'projects-additionality':
					return {
						...module,
						label: 'global.additionalitiesProjects',
					};
				default:
					return module;
			}
		} else {
			return { id: '', label: '' };
		}
	};

	const module = getModule();

	useEffect(() => {
		setTimeout(() => {
			setIsLoading(false);
		}, 1000);
	}, []);

	if (isLoading) return <Loading />;

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.layout}>
			<DefaultModal
				opened={isOpenModalLogout}
				onConfirm={() => {
					logout();
					setIsOpenModalLogout(false);
				}}
				onClose={() => {
					setIsOpenModalLogout(false);
				}}
				title={t('global.logoutSureTitle')}
			/>

			<Box className={classes.wrapper}>
				{/*Sidebar */}
				<Sidebar
					isArecPartner={Object.values(
						AREC_ORGANIZATION_IDS
					).includes(user?.organization?.id)}
					onChange={(link: any) => {
						if (link.id == 'logout') {
							setIsOpenModalLogout(true);
						} else {
							navigate(link.path);
						}
					}}
					sidebarItems={mainLinksData.filter(
						(item: TSidebarItem) => item && item.visible
					)}
					activeModule={module.id}
				/>
				<Box className={classes.content}>
					<Header
						id={module.id}
						label={module.label}
						user={cachedUser}
						onLogout={() => {
							setIsOpenModalLogout(true);
						}}
					/>
					{/*Drawer production */}
					<DrawerStepperOffset
						opened={offsetOpenDrawer}
						onClose={() => {
							setOffsetCreationActiveStep(0);
							setOffsetOpenDrawer(false);
							setOffsetAdditionalProjects([]);
							setOffsetAdditionalProjectsSelected([]);
							setOffsetMesurabilityProjects([]);
							setOffsetMesurabilityProjectsSelected([]);
							setOffsetProjectMixinAco2([]);
							setOffsetProjectMixinAddi([]);
							setOffsetStep2Error(false);
							setOffsetStep3Error(false);
							setOffsetCreationDatas({});
							setOffsetMesurabilityProjectsPage(1);
							setOffsetAdditionalProjectsPage(1);
						}}
					/>
					{/*Drawer Meter point */}
					<AddMeterPoints
						opened={openDrawerMeterPointForm}
						onClose={handleCloseDrawerAddMeterPoint}
					/>
					{/*Drawer start mint */}
					<StartMint
						opened={isOpenStartMint}
						onClose={() => {
							setIsOpenStartMint(false);
							setMintHistorySelected({});
						}}
					/>
					{/*Drawer Site */}
					<AddSite
						opened={openDrawerSiteForm}
						onClose={handleCloseDrawerAddSite}
					/>
					{/*Main */}
					<Box className={classes.body}>
						<Suspense fallback={<Overview />}>
							<Outlet />
						</Suspense>
					</Box>
					<Footer
						versionIGLU={packageJson.version}
						buildIGLU={BUILD_SHA}
						versionAPI={version}
						buildAPI={build}
						isStaging={IS_STAGING}
					/>
				</Box>
			</Box>
		</Box>
	);
};

export default Main;
